// Tiny external store for ad-hoc email-consent modal suppression.
//
// Background. The /pro suppression in App.tsx (RouteAwareEmailConsentBanner)
// works off `useLocation().pathname` because /pro is an actual route. The
// NotFound page is different: it's a conditional render inside
// pages/Player/index.tsx when `isFileNotFound` is true, mounted on the
// /player/:fileId path — there is no /404 route to match. So a pathname
// check can't distinguish "viewing a real file" from "this share link is
// dead." We need NotFound itself to signal "suppress me."
//
// Mechanism. A module-level boolean with a subscribe/notify pair, consumed
// by the wrapper via useSyncExternalStore. NotFound calls
// suppressEmailConsent() on mount and the returned cleanup on unmount —
// the wrapper re-renders and returns null while the flag is set, matching
// the /pro behavior. Same outcome (modal never appears), same locus of
// policy (wrapper-side), without inventing a new context provider for a
// single boolean.
//
// Refcounting. Stored as a counter, not a flag, so concurrent suppressors
// (e.g. dev StrictMode double-effects, or a future second page that also
// wants to suppress) compose correctly: each mount +1, each cleanup -1,
// "suppressed" = count > 0. The fall-through to zero re-enables the modal
// on the NEXT auto-open window — there's no retroactive show because
// EmailConsentBanner gates on `sessionStorage.emailConsentModalShown` once
// per session, so a suppressed NotFound visit doesn't burn the session's
// one shot.

let suppressionCount = 0;
const listeners = new Set<() => void>();

const notify = () => {
  listeners.forEach((listener) => listener());
};

export const suppressEmailConsent = (): (() => void) => {
  suppressionCount += 1;
  notify();
  return () => {
    suppressionCount = Math.max(0, suppressionCount - 1);
    notify();
  };
};

export const subscribeEmailConsentSuppression = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getEmailConsentSuppressed = (): boolean => suppressionCount > 0;
